import apiClient from '../api/api';
import { API_ENDPOINTS } from '../api/api.config';

export enum DocumentStoreTypeEnums {
  LOCAL = "LOCAL",
  GOOGLE = "GOOGLE DRIVE",
  ONEDRIVE = "ONEDRIVE",
  DROPBOX = "DROPBOX"
}

export enum FileProcessTypeEnums {
  AI_OCR = "AI_OCR",
  NORMAL_OCR = "NORMAL_OCR"
}

export enum AiPerformanceLevelEnums {
  LOW = "LOW",
  MEDIUM = "MEDIUM",
  HIGH = "HIGH"
}

export interface DocumentStore {
  _id: string;
  folder_name: string;
  folder_description: string;
  document_store_type?: string;
  createdAt: string;
  updatedAt: string;
  total_file_count: number;
  __v: number;
}

export interface CreateDocumentStoreData {
  folder_name: string;
  folder_description: string;
  document_store_type?: string;
}

export interface UpdateDocumentStoreData {
  folder_name: string;
  folder_description: string;
}

export interface DocumentStoreListParams {
  skip: number;
  limit: number;
  folder_name?: string;
  document_store_type?: string;
}

export interface DocumentStoreListResponse {
  success: boolean;
  code: number;
  data: {
    document_stores: DocumentStore[];
    total_count: number;
    last_modified: string;
  };
}

export interface DocumentStoreResponse {
  success: boolean;
  code: number;
  data: DocumentStore;
}

class DocumentStoresService {
  async getDocumentStores(params: DocumentStoreListParams): Promise<DocumentStoreListResponse> {
    try {
      const requestBody = {
        skip: params.skip,
        limit: params.limit,
        ...(params.folder_name && { folder_name: params.folder_name }),
        ...(params.document_store_type && { document_store_type: params.document_store_type })
      };

      const response = await apiClient.post(API_ENDPOINTS.DOCUMENT_STORES.LIST, requestBody);
      return response.data;
    } catch (error) {
      console.error('Error fetching document stores:', error);
      throw error;
    }
  }

  async getDocumentStoreById(id: string): Promise<DocumentStore> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.DOCUMENT_STORES.GET_BY_ID(id));
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching document store ${id}:`, error);
      throw error;
    }
  }

  async createDocumentStore(storeData: CreateDocumentStoreData): Promise<DocumentStore> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.DOCUMENT_STORES.CREATE, {
        ...storeData,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error creating document store:', error);
      throw error;
    }
  }

  async updateDocumentStore(id: string, storeData: UpdateDocumentStoreData): Promise<DocumentStore> {
    try {
      const response = await apiClient.put(API_ENDPOINTS.DOCUMENT_STORES.UPDATE(id), {
        ...storeData,
      });
      return response.data.data;
    } catch (error) {
      console.error(`Error updating document store ${id}:`, error);
      throw error;
    }
  }

  async deleteDocumentStore(id: string): Promise<void> {
    try {
      await apiClient.delete(API_ENDPOINTS.DOCUMENT_STORES.DELETE(id));
    } catch (error) {
      console.error(`Error deleting document store ${id}:`, error);
      throw error;
    }
  }

  async uploadFile(
    documentStoreId: string,
    file: File,
    aiPerformanceLevel: string,
    uploadType: string,
    fileProcessingType: string,
    folderId?: string | null
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      let url = API_ENDPOINTS.DOCUMENT_STORES.UPLOAD_FILE(
        documentStoreId,
        aiPerformanceLevel,
        uploadType,
        fileProcessingType
      );
      if (folderId) {
        const separator = url.includes('?') ? '&' : '?';
        url += `${separator}folder_id=${encodeURIComponent(folderId)}`;
      }

      const response = await apiClient.put(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  async getDocumentStoresDropdown(): Promise<any> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.DOCUMENT_STORES.DROPDOWN);
      return response.data;
    } catch (error) {
      console.error('Error fetching document stores dropdown:', error);
      throw error;
    }
  }

  /**
   * Returns every (non-deleted) folder in a document store as a flat array.
   * Caller links by parent_folder_id to build a tree. Used by the workflow
   * Document Store node's folder picker.
   */
  async listAllFoldersForStore(documentStoreId: string): Promise<FolderItem[]> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.FOLDERS.LIST_ALL, {
        document_store_id: documentStoreId,
      });
      return response.data?.data ?? [];
    } catch (error) {
      console.error('Error fetching folders for store:', error);
      throw error;
    }
  }

  /**
   * Returns uploaded files for a store. Used by the workflow Document Store
   * node's "Matching files" preview. We pull a wide page and filter
   * client-side by folder_id when the user picks a specific folder — the
   * list endpoint accepts only a single folder_id, so for include_subfolders
   * we fetch the whole store and filter against descendant ids on the FE.
   */
  async listFilesForStore(documentStoreId: string, limit: number = 200): Promise<UploadedFilePreview[]> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.UPLOADED_FILES.LIST, {
        document_store_id: documentStoreId,
        skip: 0,
        limit,
      });
      // Backend returns { success, code, data: { total_count, uploadedFilesList, last_modified } }
      const body = response.data?.data ?? response.data;
      if (Array.isArray(body)) return body;
      return body?.uploadedFilesList ?? body?.uploaded_files ?? body?.files ?? [];
    } catch (error) {
      console.error('Error fetching files for store:', error);
      throw error;
    }
  }
}

export interface FolderItem {
  _id: string;
  id: string;
  document_store_id: string;
  parent_folder_id: string | null;
  name: string;
  source?: string;
  is_deleted?: boolean;
  createdAt?: string;
}

export interface UploadedFilePreview {
  _id: string;
  id?: string;
  name: string;
  document_store_id: string;
  folder_id?: string | null;
  file_type?: string;
  file_size?: number;
  overall_processed_status?: string;
  createdAt?: string;
}

export const documentStoresService = new DocumentStoresService();
export default documentStoresService;